import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@ui/components/ui/dialog';
import { Switch } from '@ui/components/ui/switch';
import { Button } from '@ui/components/ui/button';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { usePhillConnections, useDisconnectSource } from '@data/hooks/auth';
import { useSetDailyNudge, useSetDefaultChapter } from '@data/hooks/membership';
import { useOrganization, useOrganizationChapters } from '@data/hooks/organization';
import { useStore } from '@data/storages';
import { PhillLogo } from '../login/PhillLogo';
import Connections from './Connections';
import { PhilBotIcon } from './PhilBotIcon';
import { hasOnboarded, markOnboarded } from './onboardingState';
import { clearPhilBotReturn, hasPhilBotReturn } from './returnTrip';
import { useConnectSource } from './useConnectSource';

/**
 * First-login welcome for employees: welcome → turn Phill on and connect what it may read → the chapter
 * your time is filed under → done.
 *
 * Not dismissable, on purpose. This is the moment consent belongs in — the person is being told what
 * Phill reads and why, and says yes to their calendar itself rather than to a sign-in button that
 * quietly took it. Everything it sets is saved as it is set, through the same routes Settings uses,
 * so leaving halfway loses nothing.
 *
 * The prototype's two-factor step is deliberately absent: two-factor is not part of this port.
 */
const FIRST_STEP = 1;
const CONNECT_STEP = 2;
const CHAPTER_STEP = 3;
const LAST_STEP = 4;

const OnboardingDialog = ({ userId }: { userId?: string }): JSX.Element | null => {
  const { t } = useTranslation();
  const { organization } = useStore();

  // Your own membership, read from the organization detail and found by user — the same way Settings does
  // it, and for the same reason.
  //
  // The store's organization was the obvious place to look and is the wrong one: its `memberships` carries
  // a single entry, and it is the organization's first member rather than you. So `memberships[0]` pointed
  // the wizard's writes at a stranger's row, which the own-membership routes correctly answered 403 to. It
  // only ever looked right for the person who created the organization, who is usually the one testing it.
  const { data: organizationDetail, isPlaceholderData } = useOrganization(organization?.id);
  const membership = (organizationDetail?.memberships ?? []).find(candidate => candidate.user?.id === userId);

  // Read once, before the effect below clears the marker: connecting takes the browser away and back,
  // and the wizard has to reopen on the step it left from rather than starting over.
  const [returned] = useState(hasPhilBotReturn);
  // Latched: the chapter step writes the field this reads, so a live answer closes the wizard mid-way.
  const [greeted, setGreeted] = useState<boolean | null>(() => (hasOnboarded(userId) ? true : null));
  const [step, setStep] = useState(returned ? CONNECT_STEP : FIRST_STEP);
  // False until the person moves between steps, so a fresh mount doesn't replay the slide.
  const [hasNavigated, setHasNavigated] = useState(false);

  const { data: connections } = usePhillConnections();
  const { mutate: disconnect, variables: disconnecting, isPending: isDisconnecting } = useDisconnectSource();
  const { mutate: setDailyNudge } = useSetDailyNudge(membership?.id);
  // What the membership actually says, which is what Settings shows too. Drawing it off on the first
  // step instead would be a switch that disagrees with the account behind it: nothing is written until
  // somebody moves it, so a wizard clicked through unchanged would have promised Phill was silent while
  // the API had it on.
  //
  // The fallback is off, matching the column's own default. A new membership arrives with Phill silent,
  // and turning it on is something you do here rather than something already done to you.
  const [botEnabled, setBotEnabled] = useState(membership?.isDailyNudgeEnabled ?? false);

  // The welcome opens seconds after signing in, so the membership usually lands after this mounts, and
  // `useState` would keep the fallback for the life of the dialog. Re-read whenever it changes, which is
  // also how the value the write returns gets back onto the switch.
  const nudgeSetting = membership?.isDailyNudgeEnabled;
  useEffect(() => {
    if (membership?.id) setBotEnabled(nudgeSetting ?? false);
  }, [membership?.id, nudgeSetting]);

  // The wizard sent the browser out, so the wizard clears up after the trip; Settings leaves it alone
  // until the wizard is done (see SettingsPage).
  useEffect(() => {
    if (returned) clearPhilBotReturn();
  }, [returned]);

  // With no active chapters the wizard finishes without one, so those rest on the local flag.
  // Not the placeholder: it carries the organization's first member, not this one, and answers "never greeted".
  useEffect(() => {
    if (greeted !== null || !membership || isPlaceholderData) return;
    setGreeted(Boolean(membership.defaultChapter?.key));
  }, [greeted, membership, isPlaceholderData]);

  // Active chapters only. A retired one is not somewhere new time can be filed — `getReportChapterKey`
  // ignores a default that has since been switched off — so offering it here would be offering a choice
  // that does nothing.
  const { data: chapters } = useOrganizationChapters(organization?.id);
  const { mutate: setDefaultChapter } = useSetDefaultChapter(membership?.id);
  const chapterOptions = (chapters?.items ?? []).map(chapter => ({ value: chapter.key, label: chapter.label }));

  // Held locally so the picker answers the click, and seeded from whatever the membership already carries
  // — somebody who set it in Settings before finishing the wizard should not be asked again.
  const [pickedChapter, setPickedChapter] = useState<string | undefined>();
  const chapter = pickedChapter ?? membership?.defaultChapter?.key;

  // Saved as it is picked, for the same reason the switch above is: the wizard cannot be dismissed but a
  // browser can be closed, and an answer given should stay given.
  const chooseChapter = (key: string): void => {
    if (!key) return;
    setPickedChapter(key);
    setDefaultChapter(key, { onError: () => setPickedChapter(undefined) });
  };

  // Required to move on, except when there is nothing to require: an organization with no active chapters
  // would otherwise be a wizard nobody can finish.
  const canLeaveChapterStep = Boolean(chapter) || chapterOptions.length === 0;

  // Saved as it is moved rather than at the end: the wizard is not dismissable, but a browser can be
  // closed, and a switch that was moved should stay moved.
  const toggleBot = (value: boolean): void => {
    setBotEnabled(value);
    setDailyNudge(value, { onError: () => setBotEnabled(!value) });
  };

  const goToStep = (next: number): void => {
    setHasNavigated(true);
    setStep(next);
  };

  const connect = useConnectSource();

  const finish = (): void => {
    markOnboarded(userId);
    setGreeted(true);
  };

  if (greeted !== false) return null;

  const steps: Record<number, { content: React.ReactNode; footer: React.ReactNode }> = {
    1: {
      content: (
        <div>
          <PhillLogo className="mb-4 size-12 text-foreground" />
          <h2 className="text-xl font-bold text-foreground">{t('onboarding.welcomeTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.welcomeBody')}</p>
        </div>
      ),
      footer: (
        <Button onClick={() => goToStep(CONNECT_STEP)}>
          {t('onboarding.getStarted')}
          <ArrowRight />
        </Button>
      ),
    },
    2: {
      content: (
        <div>
          <div className="mb-3 flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <PhilBotIcon className="size-6" />
          </div>
          <h2 className="text-xl font-bold text-foreground">{t('onboarding.botTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.botBody')}</p>
          {/* The same switch as Settings, and the same wording, so turning Phill on is one deliberate
              act in both places rather than a side effect of finishing a wizard. */}
          <div className="mt-4 flex items-start justify-between gap-4 rounded-lg border border-border p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t('onboarding.botSwitchTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('onboarding.botSwitchBody')}</p>
            </div>
            {/* Not offered until the membership has arrived: its id is the route the switch writes to,
                and this step is reached seconds after signing in, while that read is still in flight. */}
            <Switch
              checked={botEnabled}
              onCheckedChange={toggleBot}
              disabled={!membership?.id}
              aria-label={t('onboarding.botSwitchTitle')}
            />
          </div>
          <div className="mt-4">
            <Connections
              connections={connections}
              onConnect={connect}
              onDisconnect={disconnect}
              pending={isDisconnecting ? (disconnecting ?? null) : null}
              disabled={!botEnabled}
            />
          </div>
        </div>
      ),
      // Exactly one of these is ever live, and the switch decides which. Both stay on screen so the
      // choice is legible either way: off, the switch alone would leave "report manually" as small
      // print rather than something you can point at and pick.
      footer: (
        <>
          <Button variant="ghost" disabled={botEnabled} onClick={() => goToStep(CHAPTER_STEP)}>
            {t('onboarding.preferManual')}
          </Button>
          <Button disabled={!botEnabled} onClick={() => goToStep(CHAPTER_STEP)}>
            {t('continue')}
            <ArrowRight />
          </Button>
        </>
      ),
    },
    3: {
      content: (
        <div>
          <div className="mb-3 flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Layers className="size-6" />
          </div>
          <h2 className="text-xl font-bold text-foreground">{t('onboarding.chapterTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.chapterBody')}</p>
          <div className="mt-4">
            {/* The same control and the same list as Settings, so the answer given here and the answer
                changed later are visibly the same setting. */}
            <MultiSelectField
              single
              label={<span className="sr-only">{t('onboarding.chapterTitle')}</span>}
              options={chapterOptions}
              value={chapter ? [chapter] : []}
              onChange={values => chooseChapter(values[0] ?? '')}
              // `emptyLabel` is what shows while nothing is picked, so it has to read as an invitation
              // rather than a statement — except when there genuinely is nothing to pick, which is the
              // one case where a statement is the truth.
              emptyLabel={chapterOptions.length === 0 ? t('onboarding.chapterNone') : t('onboarding.chapterPick')}
              searchable={false}
            />
          </div>
        </div>
      ),
      footer: (
        <Button disabled={!canLeaveChapterStep} onClick={() => goToStep(LAST_STEP)}>
          {t('continue')}
          <ArrowRight />
        </Button>
      ),
    },
    4: {
      content: (
        <div>
          <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
            <Check className="size-6" />
          </div>
          <h2 className="text-xl font-bold text-foreground">{t('onboarding.doneTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {botEnabled ? t('onboarding.doneBotBody') : t('onboarding.doneManualBody')}
          </p>
        </div>
      ),
      footer: <Button onClick={finish}>{t('onboarding.finish')}</Button>,
    },
  };

  // Falls back rather than destructuring blind: a step with no entry here would throw during render,
  // which unmounts the whole app and leaves a blank page with nothing on screen to explain it.
  const { content, footer } = steps[step] ?? steps[FIRST_STEP];
  // A small fitted box for the bookends and larger ones for the steps that ask something, so
  // "small = start and finish, large = doing the work" reads at a glance. The chapter step is one
  // question and a picker, so it sits between the two rather than taking the connect step's height.
  const stepHeights: Record<number, number> = { [CONNECT_STEP]: 530, [CHAPTER_STEP]: 320 };
  const boxHeight = stepHeights[step] ?? 250;

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-xl" showCloseButton={false}>
        <DialogTitle className="sr-only">{t('onboarding.title')}</DialogTitle>
        <div className="flex flex-col transition-[height] duration-300 ease-out" style={{ height: boxHeight }}>
          {/* Each step slides in from the right as it mounts (keyed by step). The old page is unmounted
              immediately — no cross-fade — so there is no ghost of the previous page. */}
          <div
            key={step}
            className={cx(
              'flex-1 overflow-x-hidden overflow-y-auto pr-1',
              // Only a move between steps slides: a fresh mount, or the trip back from a connect
              // redirect, has no previous page to have come from.
              hasNavigated && 'duration-300 ease-out animate-in fade-in-0 slide-in-from-right-6',
            )}
          >
            {content}
          </div>
          <DialogFooter className="mt-4">{footer}</DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingDialog;
